import { TrashIcon } from '@/components/trash-icon'
import { Plus } from '@/components/plus'
import { Product } from 'shared/types/api'
import { useAppContext } from '@/pages/_app'
import { useVenueContext } from '@/services/venue'
import { usePrice } from './price'
import { MouseEvent } from 'react'

export type OrderButtonsProps = {
  quantity: number
  product: Product
  onQuantityChange: (newQuantity: number, change: '+' | '-', e: MouseEvent) => void
  counterStyle: 'action' | 'plain'
  disallowZero?: boolean
  allowMovePriceToOrderButton?: boolean
}

export function OrderButtons(props: OrderButtonsProps) {
  const { t } = useAppContext()
  const { product } = props
  const venueContext = useVenueContext()!
  const { admin, venue } = venueContext

  const price = usePrice()

  const showTwoProductsInRow = venue.features.showTwoProductsInRow

  const decrementIcon =
    props.quantity === product.minQuantity ? (
      <TrashIcon
        color={
          props.counterStyle === 'action'
            ? 'stroke-back dark:stroke-backDark'
            : 'stroke-primary dark:stroke-primaryAccentDark'
        }
      />
    ) : (
      '–'
    )

  const counterClasses =
    props.counterStyle === 'action'
      ? 'bg-action dark:bg-actionDark text-back dark:text-backDark'
      : 'bg-back dark:bg-backAccentDark dark:text-primaryAccentDark text-primary'

  const disableButton = product.isInStopList && !admin.isAdminView

  return (
    <div
      className={`h-12 text-center align-middle border border-action dark:border-actionDark rounded ${
        props.quantity > 0
          ? `text-xl ${counterClasses}`
          : `text-lg ${
              props.counterStyle === 'action'
                ? 'bg-inherit'
                : 'bg-back dark:bg-backAccentDark dark:text-primaryAccentDark'
            }`
      }`}
      style={{ width: '100%' }}
    >
      {props.quantity > 0 ? (
        <div className="h-full w-full flex justify-around items-center">
          {props.disallowZero && props.quantity === 1 ? (
            <div className="w-12"></div>
          ) : (
            <button
              className="flex justify-center items-center w-12 focus:outline-none"
              type="button"
              onClick={(e) => {
                props.onQuantityChange(
                  props.quantity === product.minQuantity ? 0 : props.quantity - 1,
                  '-',
                  e,
                )
              }}
            >
              {decrementIcon}
            </button>
          )}

          <span className="flex-shrink-0" style={{ minWidth: '40px' }}>
            {product.isUsingVolumeUnitsForQuantity && product.volume[0]
              ? `${props.quantity * product.volume[0]} ${product.volumeUnit || ''}`
              : props.quantity}
          </span>
          <button
            className="w-12 focus:outline-none"
            type="button"
            onClick={(e) => {
              if (disableButton) {
                return
              }
              props.onQuantityChange(props.quantity + 1, '+', e)
            }}
          >
            +
          </button>
        </div>
      ) : (
        <button
          className="h-full w-full flex justify-center items-center focus:outline-none"
          type="button"
          onClick={(e) => {
            if (disableButton) {
              return
            }

            return props.onQuantityChange(product.minQuantity, '+', e)
          }}
        >
          {props.allowMovePriceToOrderButton && (
            <div
              className={`text-primary dark:text-primaryDark ${
                showTwoProductsInRow ? 'lg:hidden' : 'hidden'
              }`}
            >
              {price.format(product.price)}
            </div>
          )}

          <div
            className={`items-center flex ${
              showTwoProductsInRow && props.allowMovePriceToOrderButton ? 'hidden lg:flex' : ''
            } `}
          >
            <Plus color="inline stroke-primary dark:stroke-primaryAccentDark" />
            <span className="ml-1 text-primary dark:text-primaryAccentDark">
              {t('common:order')}
            </span>
          </div>
        </button>
      )}
    </div>
  )
}
